import { Download, FolderOpen, ImagePlus, Layers, Play, RefreshCw, RotateCw, Square } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { ActionInfoQuiet, Button, PhotoSourceSheet, ScreenInfo } from '@krp/ui-kit'

import { ActionError } from '../../../shared/ActionError'
import { QueryState } from '../../../shared/QueryState'
import { preparePhoto } from '../../search/ui/preparePhoto'
import { TWIN_PRIORITY_TITLES } from '../model/twinSelectors'
import { TWIN_PRIORITIES, type TwinActions, type TwinFamiliesVm, type TwinFamilyVm, type TwinRunDetailVm, type TwinRunsVm } from '../model/twinTypes'
import type { TestingAccess } from '../model/types'
import { Share, StateBadge, TestingGate } from './TestingParts'

const SCREEN_INFO = {
  title: 'Что на этом экране',
  lines: [
    'Двойники — товары одной серии, которые отличаются словом или числом («Fix» и «Fix 2», 500 и 750 мл), а упаковка у них почти одинаковая.',
    'Сверху — семейства двойников из каталога: у каких вариантов есть фото и сколько кадров снято. «Добавить кадр» снимает или берёт из файла кадр варианта; «Снять серию по порядку» ведёт по вариантам один за другим.',
    'Ниже — замер: он показывает, не путает ли поиск варианты. Главное число — «Перепутали вариант», цель — 0. «Выгрузка» отдаёт данные для подбора таблицы калибровки.',
  ],
} as const

/** Что сейчас снимается: серия, вариант по порядку и снимок до отправки. */
interface Shoot {
  familyKey: string
  index: number
  /** Серия снимается по порядку: после принятого кадра сразу следующий вариант. */
  batch: boolean
}

/**
 * Экран «Похожие варианты» (RP-FB67): семейства двойников из каталога с кадрами вариантов, замер «не перепутал ли поиск вариант» и выгрузка для
 * калибровки. Экран получает vm и actions и больше ничего: ни store, ни HTTP. Справка экрана — одна ⓘ сверху слева (RP-FB46).
 */
export function TwinScreen({
  access,
  families,
  runs,
  actions,
}: {
  access: TestingAccess
  families: TwinFamiliesVm
  runs: TwinRunsVm
  actions: TwinActions
}) {
  return (
    <TestingGate access={access}>
      <ActionInfoQuiet>
        <section aria-labelledby="platform-twin-title" className="platform-testing">
          <header className="platform-resources__toolbar">
            <div className="platform-testing__row">
              <ScreenInfo title={SCREEN_INFO.title} lines={SCREEN_INFO.lines} />
              <h2 id="platform-twin-title">Похожие варианты</h2>
            </div>
            <div className="platform-testing__row">
              <Button variant="ghost" icon={<Layers size={16} aria-hidden="true" />} onClick={actions.openProducts} actionId="platform.testing-twin.open-products">
                Тестовые товары
              </Button>
              <Button variant="ghost" icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.reload} actionId="platform.testing-twin.reload">
                Обновить
              </Button>
            </div>
          </header>
          <ol className="platform-testing__howto" aria-label="Как собрать набор двойников">
            <li>
              <strong>Найдите семейство</strong> — серию товаров каталога. Первыми идут те, у кого фото есть не у всех вариантов.
            </li>
            <li>
              <strong>Снимите варианты</strong> — кнопка «Снять серию по порядку»: по кадру каждого варианта, как их снимет продавец.
            </li>
            <li>
              <strong>Запустите замер</strong> — он покажет, не выдаёт ли поиск двойника вместо нужного товара.
            </li>
            <li>
              <strong>Выгрузите данные</strong> для калибровки, когда замер готов.
            </li>
          </ol>
          <ActionError error={families.action.error} onReload={actions.reload} />
          <FamiliesSection vm={families} actions={actions} />
          <RunsSection vm={runs} actions={actions} />
        </section>
      </ActionInfoQuiet>
    </TestingGate>
  )
}

function FamiliesSection({ vm, actions }: { vm: TwinFamiliesVm; actions: TwinActions }) {
  const [shoot, setShoot] = useState<Shoot | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [picked, setPicked] = useState<File | null>(null)
  const [sending, setSending] = useState(false)

  const family = shoot === null ? undefined : vm.families.find((item) => item.key === shoot.familyKey)
  const variant = shoot === null ? undefined : family?.variants[shoot.index]

  // Предпросмотр — адрес, живущий только пока снимок выбран.
  const preview = useMemo(() => (picked !== null && typeof URL.createObjectURL === 'function' ? URL.createObjectURL(picked) : null), [picked])
  useEffect(
    () => () => {
      if (preview !== null) URL.revokeObjectURL(preview)
    },
    [preview],
  )

  const begin = (target: TwinFamilyVm, index: number, batch: boolean) => {
    setShoot({ familyKey: target.key, index, batch })
    setPicked(null)
    setSheetOpen(true)
  }

  const cancel = () => {
    setShoot(null)
    setPicked(null)
    setSheetOpen(false)
  }

  const send = async () => {
    if (shoot === null || family === undefined || variant === undefined || picked === null || sending) return
    setSending(true)
    let accepted = false
    try {
      // Кадр готовится так же, как в боевом поиске (размер, поворот, JPEG): замер мерит тот же путь.
      accepted = await actions.addFrame({ catalogProductId: variant.catalogProductId, series: family.key, file: await preparePhoto(picked) })
    } finally {
      setSending(false)
    }
    // Отказ виден над списком дословно; снимок остаётся, его можно отправить ещё раз.
    if (!accepted) return
    setPicked(null)
    const next = shoot.index + 1
    if (shoot.batch && next < family.variants.length) {
      setShoot({ ...shoot, index: next })
      setSheetOpen(true)
    } else {
      setShoot(null)
    }
  }

  return (
    <section aria-label="Семейства двойников">
      <h3>Семейства двойников из каталога</h3>
      <div className="platform-testing__row" role="group" aria-label="Отбор семейств">
        <Button
          variant={vm.priority === null ? 'primary' : 'secondary'}
          aria-pressed={vm.priority === null}
          onClick={() => actions.filter(null)}
          actionId="platform.testing-twin.filter"
        >
          Все
        </Button>
        {TWIN_PRIORITIES.map((priority) => (
          <Button
            key={priority}
            variant={vm.priority === priority ? 'primary' : 'secondary'}
            aria-pressed={vm.priority === priority}
            onClick={() => actions.filter(priority)}
            actionId="platform.testing-twin.filter"
          >
            {TWIN_PRIORITY_TITLES[priority]}
          </Button>
        ))}
        <Button variant="ghost" icon={<RotateCw size={16} aria-hidden="true" />} onClick={actions.recompute} actionId="platform.testing-twin.recompute">
          Пересчитать по каталогу
        </Button>
      </div>
      {vm.builtAtText !== null && <p className="platform-testing__note">{vm.builtAtText}. Семейства ищутся по названиям: общая часть и одно-два отличающихся слова или число.</p>}

      <PhotoSourceSheet
        open={sheetOpen}
        // Лист закрывается и перед системным диалогом выбора файла: съёмка от этого не прекращается, её кончают кнопкой «Закончить съёмку».
        onClose={() => setSheetOpen(false)}
        onFile={(file) => {
          setPicked(file)
          setSheetOpen(false)
        }}
      />

      {shoot !== null && family !== undefined && variant !== undefined && (
        <section aria-label="Съёмка кадра двойника" className="platform-testing__shoot">
          <h4>
            {shoot.batch ? `Серия «${family.series}»: ${shoot.index + 1}/${family.variants.length}` : `Серия «${family.series}»`}
          </h4>
          <p>
            Снимите вариант: <strong>{variant.name}</strong>
            {variant.differences === '' ? '' : ` (отличие: ${variant.differences})`}
          </p>
          {picked === null ? (
            <div className="platform-testing__actions">
              <Button icon={<ImagePlus size={16} aria-hidden="true" />} onClick={() => setSheetOpen(true)} actionId="platform.testing-twin.pick-photo">
                Выбрать или снять фото
              </Button>
              <Button variant="ghost" onClick={cancel} actionId="platform.testing-twin.shoot-cancel">
                Закончить съёмку
              </Button>
            </div>
          ) : (
            <>
              {preview !== null && <img className="platform-testing__preview" src={preview} alt={`Предпросмотр кадра: ${variant.name}`} />}
              <div className="platform-testing__actions">
                <Button
                  variant="primary"
                  icon={<ImagePlus size={16} aria-hidden="true" />}
                  disabled={sending}
                  onClick={() => void send()}
                  actionId="platform.testing-twin.add-frame"
                >
                  Добавить кадр
                </Button>
                <Button variant="ghost" disabled={sending} onClick={() => setSheetOpen(true)} actionId="platform.testing-twin.pick-photo">
                  Другое фото
                </Button>
                <Button variant="ghost" disabled={sending} onClick={cancel} actionId="platform.testing-twin.shoot-cancel">
                  Закончить съёмку
                </Button>
              </div>
            </>
          )}
        </section>
      )}

      {vm.query.status !== 'ready' ? (
        <QueryState query={vm.query} emptyText="Семейств двойников в каталоге не нашлось" onRetry={actions.reload} />
      ) : (
        <ul className="platform-testing__families" aria-label="Список семейств">
          {vm.families.map((item) => (
            <li key={item.key}>
              <article aria-label={`Семейство «${item.series}»`}>
                <header className="platform-testing__row">
                  <h4>{item.series}</h4>
                  <StateBadge badge={item.priority} />
                  <small>{item.summary}</small>
                  <Button
                    variant="secondary"
                    icon={<ImagePlus size={16} aria-hidden="true" />}
                    aria-label={`Снять серию «${item.series}» по порядку`}
                    disabled={shoot !== null}
                    disabledReason={shoot === null ? null : 'Сначала закончите текущую съёмку'}
                    onClick={() => begin(item, 0, true)}
                    actionId="platform.testing-twin.shoot-series"
                  >
                    Снять серию по порядку
                  </Button>
                </header>
                <div className="platform-sync__table-wrap">
                  <table className="platform-sync__table">
                    <thead>
                      <tr>
                        <th>Вариант</th>
                        <th>Отличие</th>
                        <th>Фото в каталоге</th>
                        <th>Кадры</th>
                        <th>Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {item.variants.map((variantRow, index) => (
                        <tr key={variantRow.catalogProductId}>
                          <td>{variantRow.name}</td>
                          <td>{variantRow.differences === '' ? '—' : variantRow.differences}</td>
                          <td>
                            <StateBadge badge={variantRow.photo} />
                          </td>
                          <td>{variantRow.framesText}</td>
                          <td>
                            <Button
                              variant="ghost"
                              icon={<ImagePlus size={16} aria-hidden="true" />}
                              aria-label={`Добавить кадр: ${variantRow.name}`}
                              disabled={shoot !== null}
                              disabledReason={shoot === null ? null : 'Сначала закончите текущую съёмку'}
                              onClick={() => begin(item, index, false)}
                              actionId="platform.testing-twin.shoot-variant"
                            >
                              Добавить кадр
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </article>
            </li>
          ))}
        </ul>
      )}
      {vm.note !== null && <p className="platform-testing__note">{vm.note}</p>}
    </section>
  )
}

function RunsSection({ vm, actions }: { vm: TwinRunsVm; actions: TwinActions }) {
  return (
    <section aria-label="Замер двойников">
      <h3>Замер</h3>
      <p className="platform-testing__note">
        Кадры двойников идут тем же путём, что боевой поиск, с тем же вердиктом. Замер в фоне: экран можно закрыть. Каталог замера — тестовый, баллы сравнивают замеры между собой.
      </p>
      <ActionError error={vm.action.error} onReload={actions.reload} />
      <div className="platform-testing__actions">
        <Button
          variant="primary"
          icon={<Play size={16} aria-hidden="true" />}
          disabled={vm.running !== null || vm.action.busy}
          disabledReason={vm.running}
          onClick={() => actions.startRun('twins')}
          actionId="platform.testing-twin.start"
        >
          Запустить замер двойников
        </Button>
        <Button
          variant="secondary"
          icon={<Play size={16} aria-hidden="true" />}
          disabled={vm.running !== null || vm.action.busy}
          disabledReason={vm.running}
          onClick={() => actions.startRun('all-frames')}
          actionId="platform.testing-twin.start-all"
        >
          Замер по всем кадрам (для калибровки)
        </Button>
        <Button
          variant="ghost"
          icon={<Download size={16} aria-hidden="true" />}
          disabled={vm.exportBlocked !== null}
          disabledReason={vm.exportBlocked}
          onClick={() => actions.download('csv')}
          actionId="platform.testing-twin.export-csv"
        >
          Выгрузка для калибровки (таблица)
        </Button>
        <Button
          variant="ghost"
          icon={<Download size={16} aria-hidden="true" />}
          disabled={vm.exportBlocked !== null}
          disabledReason={vm.exportBlocked}
          onClick={() => actions.download('json')}
          actionId="platform.testing-twin.export-json"
        >
          Выгрузка для калибровки (JSON)
        </Button>
      </div>

      {vm.query.status !== 'ready' ? (
        <QueryState query={vm.query} emptyText="Замеров двойников ещё не было" onRetry={actions.reload} />
      ) : (
        <div className="platform-sync__table-wrap">
          <table className="platform-sync__table">
            <thead>
              <tr>
                <th>Когда</th>
                <th>Состояние</th>
                <th>Состав</th>
                <th>Перепутали вариант</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {vm.rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.when}</td>
                  <td>
                    <StateBadge badge={row.state} />
                    <div className="platform-testing__note">{row.progress}</div>
                  </td>
                  <td>{row.scopeText}</td>
                  <td>{row.confused === null ? '—' : <Share share={row.confused} />}</td>
                  <td>
                    <div className="platform-testing__row">
                      <Button
                        variant="ghost"
                        icon={<FolderOpen size={16} aria-hidden="true" />}
                        aria-label={`Разобрать замер ${row.when}`}
                        onClick={() => actions.selectRun(row.id)}
                        actionId="platform.testing-twin.open-run"
                      >
                        Разобрать
                      </Button>
                      {row.cancellable && (
                        <Button
                          variant="ghost"
                          icon={<Square size={16} aria-hidden="true" />}
                          aria-label={`Отменить замер ${row.when}`}
                          onClick={() => actions.cancelRun(row.id)}
                          actionId="platform.testing-twin.cancel"
                        >
                          Отменить
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {vm.detail !== null && <RunDetail detail={vm.detail} actions={actions} />}
    </section>
  )
}

function RunDetail({ detail, actions }: { detail: TwinRunDetailVm; actions: TwinActions }) {
  const { comparison } = detail
  return (
    <section aria-label="Разбор замера двойников">
      <h3>{detail.title}</h3>
      <p>
        <StateBadge badge={detail.state} /> · {detail.progress}
      </p>
      {detail.error !== null && <p className="platform-testing__note">Причина: {detail.error}</p>}

      {detail.scores.length > 0 && (
        <section aria-label="Итог замера двойников">
          <h4>Итог</h4>
          <dl className="platform-testing__scores">
            {detail.scores.map((score) => (
              <div key={score.key}>
                <dt>{score.label}</dt>
                <dd>
                  <Share share={score.share} />
                  <div className="platform-testing__note">{score.hint}</div>
                </dd>
              </div>
            ))}
          </dl>
          <p className="platform-testing__note">{detail.frames}</p>
        </section>
      )}

      {comparison !== null && (
        <section aria-label="Сравнение с прошлым замером двойников">
          <header className="platform-resources__toolbar">
            <h4>Сравнение с прошлым замером</h4>
            <Button
              variant="ghost"
              onClick={() => actions.selectRun(comparison.previousRunId)}
              actionId="platform.testing-twin.open-previous"
            >
              Разобрать прошлый
            </Button>
          </header>
          {comparison.note !== null && <p className="platform-testing__note">{comparison.note}</p>}
          <div className="platform-sync__table-wrap">
            <table className="platform-sync__table">
              <thead>
                <tr>
                  <th>Метрика</th>
                  <th>Было</th>
                  <th>Стало</th>
                  <th>Разница</th>
                </tr>
              </thead>
              <tbody>
                {comparison.rows.map((row) => (
                  <tr key={row.label}>
                    <td>{row.label}</td>
                    <td>{row.previous}</td>
                    <td>{row.current}</td>
                    <td>{row.delta}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {detail.series.length > 0 && (
        <section aria-label="По сериям">
          <h4>По сериям — худшие первыми</h4>
          <div className="platform-sync__table-wrap">
            <table className="platform-sync__table">
              <thead>
                <tr>
                  <th>Серия</th>
                  <th>Перепутали вариант</th>
                  <th>Предложен выбор</th>
                  <th>Нашли верно</th>
                </tr>
              </thead>
              <tbody>
                {detail.series.map((slice) => (
                  <tr key={slice.series}>
                    <td>
                      {slice.series}
                      <div className="platform-testing__note">кадров {slice.frames}</div>
                    </td>
                    <td>
                      <Share share={slice.confused} />
                    </td>
                    <td>
                      <Share share={slice.choiceOffered} />
                    </td>
                    <td>
                      <Share share={slice.foundCorrect} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {detail.confusions.length > 0 && (
        <section aria-label="Кого с кем путает">
          <h4>Кого с кем путает</h4>
          <ul>
            {detail.confusions.map((confusion) => (
              <li key={confusion.key}>
                {confusion.text} — {confusion.count} раз
              </li>
            ))}
          </ul>
        </section>
      )}

      <section aria-label="Время и условия замера двойников">
        <h4>Время и условия</h4>
        <dl>
          <dt>Время ответа</dt>
          <dd>{detail.latency ?? 'нет данных'}</dd>
          {detail.stamp.map((item) => (
            <div key={item.label}>
              <dt>{item.label}</dt>
              <dd>{item.value}</dd>
            </div>
          ))}
        </dl>
      </section>
    </section>
  )
}
